/**
 * Type definitions for the project schedule application
 */

import { Project } from "../models/Project";

// Project class is exported from models/Project.ts
// Import it where needed: import { Project } from '../models/Project';

/**
 * Work categories for effort distribution
 */
export enum WorkCategory {
  DEVELOPMENT = "development",
  IA = "ia",
  PROJECT_MANAGEMENT = "project_management",
  CONTENT_CREATION = "content_creation",
  COG_SCI = "cognitive_science",
}

/**
 * Display labels for work categories
 */
export const WORK_CATEGORY_LABELS: Record<WorkCategory, string> = {
  [WorkCategory.DEVELOPMENT]: "Development",
  [WorkCategory.IA]: "IA",
  [WorkCategory.PROJECT_MANAGEMENT]: "Project Management",
  [WorkCategory.CONTENT_CREATION]: "Content Creation",
  [WorkCategory.COG_SCI]: "Cognitive Science",
};

export enum Status {
  BACKLOG = "Backlog",
  UNDER_DISCUSSION = "Under Discussion",
  PLANNED = "Planned",
  IN_PROGRESS = "In Progress",
  DONE = "Done",
}

export enum TaskStatus {
  TODO = "To do",
  IN_PROGRESS = "In progress",
  DONE = "Done",
  CANCELED = "Canceled",
}

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  [TaskStatus.TODO]: "To do",
  [TaskStatus.IN_PROGRESS]: "In progress",
  [TaskStatus.DONE]: "Done",
  [TaskStatus.CANCELED]: "Canceled",
};

export function fromStringStatus(status: string): Status {
  switch (status.toLowerCase()) {
    case "backlog":
      return Status.BACKLOG;

    case "under discussion":
      return Status.UNDER_DISCUSSION;

    case "planned":
      return Status.PLANNED;

    case "in progress":
      return Status.IN_PROGRESS;

    case "done":
      return Status.DONE;

    default:
      return Status.BACKLOG;
  }
}

export const STATUS_LABELS: Record<Status, string> = {
  [Status.BACKLOG]: "Backlog",
  [Status.UNDER_DISCUSSION]: "Under Discussion",
  [Status.PLANNED]: "Planned",
  [Status.IN_PROGRESS]: "In Progress",
  [Status.DONE]: "Done",
};

export function toString(status: Status | string): string {
  return STATUS_LABELS[status as Status] ?? String(status);
}

/**
 * Effort breakdown by work category
 */
export type EffortBreakdown = {
  [key in WorkCategory]: number;
};

export interface ProjectTask {
  id: string;
  name: string;
  weekStart: string; // YYYY-MM-DD (start of week)
  weekCount: number;
  status: TaskStatus;
}

export interface ClientProject {
  id: string;
  name: string;
}

/**
 * Month representation in the timeline
 */
export interface Month {
  year: number;
  month: number;
  label: string;
}

/**
 * Date range with calculated months
 */
export interface DateRange {
  startDate: Date;
  endDate: Date;
  months: Month[];
}

/**
 * Validation result
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Monthly effort mapping (month index to effort value)
 */
export interface MonthlyEffort {
  [monthIndex: number]: number;
}

/**
 * Project monthly effort mapping (project id to monthly efforts)
 */
export interface ProjectMonthlyEffort {
  [projectId: number]: number[];
}

/**
 * Position for timeline bars
 */
export interface Position {
  left: string;
  width: string;
}

export interface CategoryTimingShiftPayload {
  categoryId: number;
  boundary: "start" | "end";
  targetDate: string; // YYYY-MM-DD
}

/**
 * Props for ProjectSchedule component
 */
export interface ProjectScheduleProps {
  schedule: any; // Schedule class instance (imported where used to avoid circular deps)
  clientProjects: ClientProject[];
  onCreateClientProject?: (name: string) => boolean;
  onCategoryClientProjectLabelChange?: (
    categoryId: number,
    clientProjectId: string,
    shouldAssign: boolean,
  ) => void;
  onProjectClick: (project: any) => void; // Project class instance
  onAddProject: (categoryId: number) => void;
  onCategoryColorChange?: (categoryId: number, color: string) => void;
  onImportCSV: (file: File) => Promise<void>;
  onProjectStatusChange?: (project: any) => void; // Project class instance
  onOpenBackup?: () => void;
  onConvertCategoryToProject?: (categoryId: number) => void;
  onConvertProjectToCategory?: (projectId: number) => void;
  onShiftCategoryTiming?: (payload: CategoryTimingShiftPayload) => void;
  onAddTopLevelCategory?: () => void;
  onMoveCategory?: (categoryId: number, targetParentId: number | null) => void;
  onCategoryNameChange?: (categoryId: number, name: string) => void;
  onDeleteCategory?: (categoryId: number) => void;
  onReorderCategory?: (categoryId: number, direction: "up" | "down") => void;
  onMoveProject?: (
    projectId: number,
    sourceCategoryId: number,
    targetCategoryId: number,
  ) => void;
  onCapacityConfigChange?: () => void;
}

/**
 * Props for ProjectEditModal component
 */
export interface ProjectEditModalProps {
  project?: Project; // Project class instance
  availableClientProjects?: ClientProject[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (project: any, options?: { keepModalOpen?: boolean }) => void; // Project class instance
  onDelete?: (projectId: number) => void;
  availableProjects?: Project[];
  onMoveTask?: (
    sourceProjectId: number,
    task: ProjectTask,
    targetProjectId: number,
  ) => void;
}
